import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { clearTokens, getAccessToken, isTokenExpired } from "@/lib/api";
import { Loader2 } from "lucide-react";
import useOnline from "@/hooks/use-online";
import { useGetMe } from "@/services/queries/auth";
import { usePermissionsStore } from "@/store/use-permissions";

export default function AuthCheckProvider({
  children,
  checking,
}: {
  children: React.ReactNode;
  checking?: (props: {
    token: string | null;
    isTokenExpired: boolean;
    navigate: (path: string) => void;
  }) => void;
}) {
  const isOnline = useOnline();
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const token = getAccessToken();
  const tokenExpired = isTokenExpired();
  const { setPermissions, clearPermissions } = usePermissionsStore();

  const {
    data: me,
    isLoading: isLoadingMe,
    isError: isMeError,
  } = useGetMe({
    enabled: !!token && !tokenExpired && isOnline,
    retry: false,
  });

  // Populate permissions store when /me data is available
  useEffect(() => {
    if (me) {
      setPermissions({
        role: me.role,
        permissions: me.permissions,
        vesselPermissions: me.vesselPermissions,
      });
    }
  }, [me, setPermissions]);

  useEffect(() => {
    const checkAuth = async () => {
      if (checking) {
        checking({ token, isTokenExpired: tokenExpired, navigate });
        setIsChecking(false);
        return;
      }

      if (!token || tokenExpired) {
        await clearTokens();
        clearPermissions();
        navigate("/login");
        return;
      }

      if (!isOnline) {
        setIsChecking(false);
        return;
      }

      if (isLoadingMe) {
        return;
      }

      if (isMeError) {
        await clearTokens();
        clearPermissions();
        navigate("/login");
        return;
      }

      if (
        me?.requiresOnboarding &&
        location.pathname !== "/onboarding-required"
      ) {
        navigate("/onboarding-required", { replace: true });
      } else if (
        !me?.requiresOnboarding &&
        location.pathname === "/onboarding-required"
      ) {
        navigate("/", { replace: true });
      }

      setIsChecking(false);
    };

    checkAuth();
  }, [
    checking,
    clearPermissions,
    isLoadingMe,
    isMeError,
    isOnline,
    location.pathname,
    me?.requiresOnboarding,
    navigate,
    token,
    tokenExpired,
  ]);

  if (isChecking && isOnline) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-[#041935]">
        <Loader2 className="h-20 w-20 animate-spin text-white" />
      </div>
    );
  }

  return <>{children}</>;
}
